import type { User } from '@/api/types';

/**
 * Entregabilidade por destinatário.
 *
 * Ligar um canal não garante entrega: o dado que o despacho precisa fica no USUÁRIO, e quando falta
 * o servidor só registra um aviso no log. A regra parece perfeita e nunca chega.
 *
 * Push lê `attributes.notificationTokens`; Telegram lê `attributes.telegramChatId` (no fork do
 * /rastrear é uma coluna `telegram`, que não existe no stock). Web e e-mail não dependem de nada
 * no usuário, por isso ficam de fora.
 */

export type CheckedChannel = 'firebase' | 'telegram';

export const CHANNEL_LABEL: Record<CheckedChannel, string> = {
  firebase: 'App (push)',
  telegram: 'Telegram',
};

type UserLike = Partial<User> & { id: number };

const attr = (user: UserLike | undefined | null, key: string): string =>
  String((user?.attributes as Record<string, unknown> | undefined)?.[key] ?? '').trim();

export const hasPushToken = (user: UserLike | undefined | null): boolean =>
  !!attr(user, 'notificationTokens');

export const hasTelegramChat = (user: UserLike | undefined | null): boolean =>
  !!attr(user, 'telegramChatId');

export const canReceive = (user: UserLike | undefined | null, channel: CheckedChannel): boolean =>
  channel === 'firebase' ? hasPushToken(user) : hasTelegramChat(user);

export interface DeliveryGap {
  channel: CheckedChannel;
  missing: { id: number; name: string }[];
}

/** Sem destinatário escolhido devolve vazio: a tela já trata esse caso com mensagem própria. */
export const findDeliveryGaps = (
  users: UserLike[],
  recipientIds: number[],
  enabledChannels: CheckedChannel[],
): DeliveryGap[] => {
  if (!recipientIds?.length || !enabledChannels?.length) return [];
  const byId = new Map(users.map((u) => [u.id, u]));
  const gaps: DeliveryGap[] = [];
  for (const channel of enabledChannels) {
    const missing = recipientIds
      .map((id) => byId.get(id))
      .filter((u): u is UserLike => !!u)
      .filter((u) => !canReceive(u, channel))
      .map((u) => ({ id: u.id, name: u.name || u.email || `#${u.id}` }));
    if (missing.length) gaps.push({ channel, missing });
  }
  return gaps;
};

export const describeGap = (gap: DeliveryGap): string => {
  const nomes = gap.missing.map((m) => m.name).join(', ');
  const falta =
    gap.channel === 'firebase'
      ? 'não tem aparelho registrado (precisa entrar no app e permitir notificações)'
      : 'não tem o Telegram vinculado ao cadastro';
  return gap.missing.length === 1
    ? `${CHANNEL_LABEL[gap.channel]}: ${nomes} ${falta}.`
    : `${CHANNEL_LABEL[gap.channel]}: ${gap.missing.length} destinatários ${falta} — ${nomes}.`;
};
